"use client";

import { Building2, Briefcase, IndianRupee, Scale } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface DocumentType {
  id: string;
  label: string;
  description: string;
}

interface DocumentCategory {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  types: DocumentType[];
}

const documentCategories: DocumentCategory[] = [
  {
    title: "Property",
    icon: Building2,
    types: [
      {
        id: "rental",
        label: "Rental / Lease Agreement",
        description: "Rent, lease, leave & license for residential or commercial",
      },
      {
        id: "sale_deed",
        label: "Sale Deed / Agreement",
        description: "Property sale, purchase, or builder-buyer agreements",
      },
      {
        id: "society_noc",
        label: "Society / Association",
        description: "Maintenance, society bylaws, or association agreements",
      },
    ],
  },
  {
    title: "Employment",
    icon: Briefcase,
    types: [
      {
        id: "employment",
        label: "Employment Contract",
        description: "Offer letters, employment agreements, or appointment letters",
      },
      {
        id: "freelance",
        label: "Freelance / Consulting",
        description: "Independent contractor, consulting, or gig agreements",
      },
      {
        id: "nda",
        label: "NDA / Non-Compete",
        description: "Non-disclosure, confidentiality, or non-compete agreements",
      },
    ],
  },
  {
    title: "Financial",
    icon: IndianRupee,
    types: [
      {
        id: "insurance",
        label: "Insurance Policy",
        description: "Health, life, vehicle, or property insurance policies",
      },
      {
        id: "loan",
        label: "Loan Agreement",
        description: "Home loan, personal loan, vehicle, or business loan documents",
      },
      {
        id: "mutual_fund",
        label: "Mutual Fund / Investment",
        description: "SIP, mutual fund, FD, PMS, or demat agreements",
      },
      {
        id: "credit_card",
        label: "Credit Card Terms",
        description: "Credit card, BNPL, or revolving credit agreements",
      },
    ],
  },
  {
    title: "Business",
    icon: Scale,
    types: [
      {
        id: "partnership",
        label: "Partnership Deed",
        description: "Partnership, LLP, or joint venture agreements",
      },
      {
        id: "vendor",
        label: "Vendor / Service Agreement",
        description: "SLA, vendor, service, or supply agreements",
      },
      {
        id: "startup",
        label: "Term Sheet / SHA",
        description: "SAFE notes, SHA, investment, or franchise agreements",
      },
      {
        id: "other",
        label: "Other Document",
        description: "Any other legal document or contract",
      },
    ],
  },
];

interface DocumentTypeSelectorProps {
  selected: string;
  onSelect: (type: string) => void;
}

export function DocumentTypeSelector({
  selected,
  onSelect,
}: DocumentTypeSelectorProps) {
  const t = useTranslations("scan");

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {t("selectDocType")}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("selectDocTypeHelp")}
        </p>
      </div>

      <div className="space-y-6">
        {documentCategories.map((category) => (
          <div key={category.title} className="space-y-3">
            <div className="flex items-center gap-2">
              <category.icon className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">
                {category.title}
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {category.types.map((type) => (
                <button
                  key={type.id}
                  onClick={() => onSelect(type.id)}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-3 text-left transition-all",
                    selected === type.id
                      ? "border-brand bg-brand/5 ring-1 ring-brand/50"
                      : "border-border bg-card hover:border-foreground/20"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground">
                      {type.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
                      {type.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
